import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getPattern, getProduct } from "@/lib/catalog";
import { cartPrice, decodeCart, itemDetails } from "@/lib/shareCart";
import { formatPrice } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";

// Página pública do link partilhado. Se quem abre tem sessão de admin (a Alexandra), segue direto para o registo de pedido já preenchido.
// Para toda a gente é só uma página simples com as peças: o link nunca aponta para o painel e ninguém sem sessão vê nada dele.
export default function SharedCartPage() {
  const [params] = useSearchParams();
  const code = params.get("c") ?? "";
  const nav = useNavigate();
  const lines = useMemo(() => decodeCart(code), [code]);
  const [checking, setChecking] = useState(true);
  usePageMeta("Carrinho partilhado", "Carrinho partilhado com a Babybib.");
  // Pergunta em silêncio se há sessão de admin; qualquer falha = visitante normal.
  useEffect(() => {
    let alive = true;
    import("@/admin/api")
      .then(({ api }) => api.get("/auth/me"))
      .then((r) => { if (alive && typeof r?.data?.email === "string" && lines.length) nav(`/admin/encomendas/nova?carrinho=${encodeURIComponent(code)}`, { replace: true }); })
      .catch(() => null)
      .finally(() => alive && setChecking(false));
    return () => { alive = false; };
  }, [code, lines.length, nav]);
  const total = lines.reduce((s, l) => s + (cartPrice(l) ?? 0) * l.qty, 0);
  if (checking && lines.length) return <div className="bb-container py-24 text-center text-sm text-taupe" role="status">A abrir...</div>;
  return (
    <div className="bb-container pb-10 pt-10 sm:pt-16">
      <p className="bb-eyebrow mb-3 sm:mb-4">Carrinho partilhado</p>
      <h1 className="font-serif text-[2.4rem] font-normal leading-none tracking-tight sm:text-5xl" data-testid="shared-cart-title">Peças escolhidas</h1>
      {lines.length === 0 ? (
        <div className="mt-10 rounded-3xl bg-linen p-10 text-center" data-testid="shared-cart-empty">
          <p className="font-serif text-2xl">Este carrinho não é válido ou as peças já não existem.</p>
          <Link to="/loja" className="bb-btn-primary mt-8">Explorar a loja</Link>
        </div>
      ) : (
        <div className="mt-8 max-w-xl space-y-4">
          <ul className="space-y-3" data-testid="shared-cart-items">
            {lines.map((l, k) => (
              <li key={k} className="rounded-2xl bg-linen p-4 text-sm">
                <p className="font-serif text-lg">{l.qty}x {getProduct(l.slug).name}{cartPrice(l) !== null && <span className="float-right font-sans text-sm font-semibold">{formatPrice(cartPrice(l) * l.qty)}</span>}</p>
                {getPattern(l.patternId) && <p className="text-xs text-taupe">Padrão: {getPattern(l.patternId).name}</p>}
                {l.personalization && <p className="text-xs text-taupe">Nome: {l.personalization}</p>}
                {itemDetails(l) && <p className="text-xs text-taupe">{itemDetails(l).replace(/^\d+x [^(]*\(/, "").replace(/\)$/, "")}</p>}
              </li>
            ))}
          </ul>
          <p className="flex justify-between border-t border-ink/10 pt-3 font-semibold"><span>Subtotal (sem portes)</span><span>{formatPrice(total)}</span></p>
          <p className="text-sm text-taupe-strong">Este carrinho foi partilhado com a Alexandra, que vai continuar a encomenda contigo.</p>
        </div>
      )}
    </div>
  );
}
